/*
 * Refatorado: 2026-06-03 — dialog central → drawer lateral direito.
 * Padroniza com o ViewBillDialog do módulo financeiro (anchor="right",
 * radius 16, backdrop blur, header sticky). API externa intacta.
 */
import React from 'react';
import { Map, List } from 'immutable';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import comm from '../../core/comm/actions';
import qcomm from '../../core/comm/queries';
import qcache from '../../core/cache/queries';
import session from '../../core/session/actions';
import { Request, Response } from '../../core/comm';
import { searchRegex } from '../../core/util';
import { TREX_URL } from '../../core/config';

const organizationListURL = '/v1/usuario_organizacoes';
const DRAWER_WIDTH = 480;

function transformOrganization(org) {
  return {
    value: org.get('codigo'),
    label: org.get('fantasia'),
    userType: org.get('tipoUsuario'),
    zid: org.get('zid'),
  };
}

function createOrganizationPredicate(search) {
  return (org) => {
    const identification = org.get('identificacao') || '';
    const fname = org.get('fantasia') || '';
    const name = org.get('nome') || '';
    const text = `${identification} ${fname} ${name}`;
    return text.match(search);
  };
}

function filterOrganizationsAndGetProps(term, organizations) {
  if (!organizations || organizations.size < 2) {
    return [];
  }
  if (!term) {
    return organizations.take(10).map(transformOrganization).toArray();
  }

  const search = searchRegex(term, 'gi');
  return organizations
    .filter(createOrganizationPredicate(search))
    .take(10)
    .map(transformOrganization);
}

function OrganizationDialog(props) {
  const {
    open,
    onClose,
    history,
    isLoading,
    organizations,
    suspendSession,
    fetchOrganizationList,
    fetchNewOrganizationToken,
  } = props;
  const [orgsProps, setOrgsProps] = React.useState([]);
  const [term, setTerm] = React.useState('');

  React.useEffect(() => {
    if (open) {
      fetchOrganizationList();
      setTerm('');
    }
  }, [open]);

  React.useEffect(() => {
    if (organizations && organizations.size > 1) {
      setOrgsProps(filterOrganizationsAndGetProps(term, organizations));
    }
  }, [term, organizations]);

  let listContent;
  if (isLoading) {
    listContent = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (orgsProps.length === 0) {
    listContent = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          px: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          Nenhuma organização encontrada.
        </Typography>
      </Box>
    );
  } else {
    /* MUI v9: <MenuItem> exige estar dentro de <Menu> ou <MenuList>,
       senão lança "MenuListContext is missing" e crasha a tela. */
    listContent = (
      <MenuList sx={{ py: 0 }}>
        {orgsProps.map((p) => (
          <MenuItem
            key={p.value}
            onClick={() => {
              const { userType, value, zid } = p;
              const isEnterprise = userType !== 'F';
              if (!isEnterprise) {
                history.push('/');
              }
              suspendSession();
              fetchNewOrganizationToken(
                value,
                isEnterprise ? `${TREX_URL}/index.php?zid=${zid}` : null,
              );
              onClose();
            }}
            sx={{
              borderRadius: 1.5,
              py: 1.25,
              px: 2,
              mb: 0.5,
              transition: 'background-color 120ms ease',
              '&:hover': {
                bgcolor: 'rgba(0, 128, 173, 0.08)',
              },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#1a3a47',
                  }}
                >
                  {p.label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </MenuList>
    );
  }

  return (
    <Drawer
      id="/suaformatura/components/dashboard/orgbutton/orgdialog/drawer"
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: '92vw', md: DRAWER_WIDTH },
            maxWidth: '100vw',
            borderTopRightRadius: { sm: 16 },
            borderBottomRightRadius: { sm: 16 },
            boxShadow: '16px 0 48px rgba(0, 30, 45, 0.12)',
            border: 'none',
            bgcolor: '#f7fafc',
            display: 'flex',
            flexDirection: 'column',
          },
        },
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 30, 45, 0.35)',
            backdropFilter: 'blur(3px)',
          },
        },
      }}
    >
      {/* Header sticky */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'white',
          borderBottom: '1px solid rgba(0, 128, 173, 0.10)',
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(96, 125, 139, 0.85)',
                letterSpacing: '1.4px',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              Acesso rápido
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1a3a47',
                fontSize: { xs: '1.15rem', md: '1.4rem' },
                lineHeight: 1.25,
                mt: 0.5,
              }}
            >
              Mudar de organização
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Fechar"
            size="small"
            sx={{
              color: '#0080ad',
              bgcolor: 'rgba(0, 128, 173, 0.06)',
              borderRadius: '10px',
              p: { xs: 0.75, md: 1 },
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.12)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <TextField
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          fullWidth
          size="small"
          placeholder="Buscar organização (ex.: Contabilidade 2019 - Harvard)"
          helperText="Digite parte do nome ou da identificação"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Conteúdo: lista de organizações */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: { xs: 1.5, md: 2 },
          py: { xs: 1, md: 1.5 },
        }}
      >
        {listContent}
      </Box>
    </Drawer>
  );
}

function fetchOrganizationList() {
  return comm.get(
    Request({
      view: '/',
      url: organizationListURL,
    }),
  );
}

function fetchNewOrganizationToken(organization, link) {
  return comm.get(
    Request({
      view: '/suaformatura/components/dashboard/orgbutton',
      url: '/v1/all/trocar_organizacao/:organization',
      urlParams: Map({ organization }),
      onSuccess: Response({
        dispatch: List([
          () => session.persistToken(),
          () => comm.refresh(link),
        ]),
      }),
    }),
  );
}

function mapStateToProps(state) {
  return {
    organizations: qcache.getData(state, '/', organizationListURL),
    isLoading: qcomm.isLoading(state, '/', organizationListURL),
  };
}

export default connect(mapStateToProps, {
  fetchOrganizationList,
  fetchNewOrganizationToken,
  suspendSession: session.suspend,
})(withRouter(OrganizationDialog));
